import type { RoofModel, PanelLayout, PanelInfo, SolarModule } from '../types';

// ============================================================
// AUTO PANEL PLACEMENT
// Places panels on roof faces, prioritized by solar access.
// Uses point-in-polygon testing to handle arbitrary traced shapes.
// Respects setbacks and avoids obstructions.
// ============================================================

const SETBACK_RIDGE = 3;  // feet
const SETBACK_EAVE = 1.5; // feet

/**
 * Point-in-polygon test using ray casting.
 */
function pointInPolygon(point: [number, number], polygon: [number, number][]): boolean {
  const [px, py] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (((yi > py) !== (yj > py)) &&
        (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * Shrink a polygon inward by a given setback distance.
 * Simple approach: move each vertex toward the centroid.
 */
function shrinkPolygon(
  vertices: [number, number][],
  setback: number
): [number, number][] {
  if (vertices.length < 3) return vertices;

  // Compute centroid
  const cx = vertices.reduce((s, v) => s + v[0], 0) / vertices.length;
  const cy = vertices.reduce((s, v) => s + v[1], 0) / vertices.length;

  return vertices.map(([x, y]) => {
    const dx = cx - x;
    const dy = cy - y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return [x, y] as [number, number];

    const ratio = Math.max(0, (dist - setback) / dist);
    return [
      x + dx * (1 - ratio),
      y + dy * (1 - ratio),
    ] as [number, number];
  });
}

export function autoPlacePanels(roofModel: RoofModel, module: SolarModule): PanelLayout {
  const panelL = module.length * 3.28; // meters to feet
  const panelW = module.width * 3.28;
  const rowSpacing = panelL + 0.5;
  const colSpacing = panelW + 0.3;

  const panels: PanelInfo[] = [];

  // Sort faces by solar access (highest first)
  const sortedFaces = [...roofModel.faces].sort((a, b) => b.solar_access - a.solar_access);

  for (const face of sortedFaces) {
    if (face.solar_access < 60) continue;

    // Shrink the polygon by the setback distance to create usable area
    const shrunkVertices = shrinkPolygon(face.vertices, SETBACK_EAVE);

    // Get bounding box of the shrunk polygon for grid generation
    const xs = shrunkVertices.map(v => v[0]);
    const ys = shrunkVertices.map(v => v[1]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const bboxWidth = maxX - minX;
    const bboxDepth = maxY - minY;
    if (bboxWidth < panelW || bboxDepth < panelL) continue;

    // Generate a grid of candidate positions
    const cols = Math.floor(bboxWidth / colSpacing);
    const rows = Math.floor(bboxDepth / rowSpacing);
    const offsetX = (bboxWidth - cols * colSpacing) / 2 + panelW / 2;
    const offsetY = (bboxDepth - rows * rowSpacing) / 2 + panelL / 2;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const px = minX + offsetX + c * colSpacing;
        const py = minY + offsetY + r * rowSpacing;

        // Check if the panel center is inside the shrunk polygon
        if (!pointInPolygon([px, py], shrunkVertices)) continue;

        // Also check that the panel corners are inside (approximate with center + half-size)
        const corners: [number, number][] = [
          [px - panelW / 2, py - panelL / 2],
          [px + panelW / 2, py - panelL / 2],
          [px - panelW / 2, py + panelL / 2],
          [px + panelW / 2, py + panelL / 2],
        ];
        const allCornersInside = corners.every(corner => pointInPolygon(corner, shrunkVertices));
        if (!allCornersInside) continue;

        // Skip if near an obstruction
        let skip = false;
        for (const obs of roofModel.obstructions) {
          const dx = px - obs.position[0];
          const dy = py - obs.position[1];
          if (Math.sqrt(dx * dx + dy * dy) < obs.width / 2 + 2) {
            skip = true;
            break;
          }
        }
        if (skip) continue;

        // Check shade at this point if shade data is available
        if (face.shade_points && face.shade_points.length > 0) {
          // Find the nearest shade sample
          let nearestShade = face.solar_access;
          let minDist = Infinity;
          for (const sp of face.shade_points) {
            const d = Math.sqrt(
              Math.pow(sp.x - px, 2) + Math.pow(sp.y - py, 2)
            );
            if (d < minDist) {
              minDist = d;
              nearestShade = sp.solar_access;
            }
          }
          // Skip panels in heavily shaded areas
          if (nearestShade < 55) continue;
        }

        panels.push({
          id: `panel_${panels.length}`,
          face_id: face.id,
          position: [px, py, face.height],
          orientation: 'portrait',
          wattage: module.wattage,
        });
      }
    }
  }

  const totalWattage = panels.length * module.wattage;
  return {
    panels,
    total_count: panels.length,
    total_system_kw: totalWattage / 1000,
    equipment: { module },
  };
}
